import React, { useEffect, useMemo, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { humanizeString } from "@utils/stringMinipulation"
import { api, WealthManagerAttrs } from "../api"
import { toastSuccess, toastError } from "../toast"
import { InviteModal, InviteFormValues } from "../components/InviteModal"
import { SearchBox } from "@UILibrary/SearchBox"
import { ConfirmDialog } from "@UILibrary/Modal"
import { Table, TableColumn, SortState, RowActionsMenu, ActionItem, sortRows } from "@UILibrary/Table"

type WealthManagerRow = {
  id: string
  attributes: {
    fullName: string
    email: string
    cellphoneNumber: string
    status: string
    active: boolean
    available: boolean
    clientCount: number
  }
}

// A row action plus the name it reports on.
type Target = { id: number; name: string }
// Invite wording differs for a first send vs a resend, so the toast needs to
// know which one it is reporting.
type InviteTarget = Target & { resend: boolean }

const PER_PAGE = 25

export const WealthManagersTab: React.FC = () => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const { data, isLoading, error } = useQuery(
    ["fsp-admin-wealth-managers", search],
    () => api.wealthManagers.index(search),
    { keepPreviousData: true }
  )
  const [inviteOpen, setInviteOpen] = useState(false)
  const [deactivateTarget, setDeactivateTarget] = useState<Target | null>(null)
  const [sort, setSort] = useState<SortState>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const createMutation = useMutation(
    (attrs: WealthManagerAttrs) => api.wealthManagers.create(attrs),
    {
      onSuccess: (_data, attrs) => {
        queryClient.invalidateQueries(["fsp-admin-wealth-managers"])
        toastSuccess(`Invite sent to ${attrs.email}.`)
      },
      // The modal keeps its own inline error next to the form it came from, so
      // no failure toast here.
    }
  )
  // Row actions report through toasts — they're fire-and-forget, so the name has
  // to travel with the id to name the person in the message.
  const inviteMutation = useMutation(
    ({ id }: InviteTarget) => api.wealthManagers.invite(id),
    {
      onSuccess: (_data, { name, resend }) => {
        queryClient.invalidateQueries(["fsp-admin-wealth-managers"])
        toastSuccess(`Invite ${resend ? "resent" : "sent"} to ${name}.`)
      },
      onError: (error, { name, resend }) =>
        toastError(`Couldn't ${resend ? "resend" : "send"} the invite to ${name}`, error),
    }
  )
  const deactivateMutation = useMutation(
    ({ id }: Target) => api.wealthManagers.deactivate(id),
    {
      onSuccess: (_data, { name }) => {
        queryClient.invalidateQueries(["fsp-admin-wealth-managers"])
        toastSuccess(`${name} deactivated.`)
      },
      onError: (error, { name }) => toastError(`Couldn't deactivate ${name}`, error),
    }
  )
  const reactivateMutation = useMutation(
    ({ id }: Target) => api.wealthManagers.reactivate(id),
    {
      onSuccess: (_data, { name }) => {
        queryClient.invalidateQueries(["fsp-admin-wealth-managers"])
        toastSuccess(`${name} reactivated.`)
      },
      onError: (error, { name }) => toastError(`Couldn't reactivate ${name}`, error),
    }
  )


  const rows: WealthManagerRow[] = data?.data ?? []

  const statusOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.attributes.status)))
        .sort()
        .map((s) => ({ value: s, label: humanizeString(s) })),
    [rows]
  )

  // Client-side filter + sort; the Table paginates the result.
  const displayRows = useMemo(() => {
    const filtered = statusFilter ? rows.filter((r) => r.attributes.status === statusFilter) : rows
    return sort ? sortRows(filtered, (r) => (r.attributes as Record<string, unknown>)[sort.key], sort.dir) : filtered
  }, [rows, statusFilter, sort])

  // Reset to page 1 whenever the result set changes.
  useEffect(() => setPage(1), [search, statusFilter, sort])

  const columns: TableColumn<WealthManagerRow>[] = [
    { key: "fullName", label: "Name", sortable: true, render: (r) => r.attributes.fullName },
    { key: "email", label: "Email", sortable: true, render: (r) => r.attributes.email },
    {
      key: "status",
      label: "Status",
      sortable: true,
      filterable: true,
      filterOptions: statusOptions,
      render: (r) => humanizeString(r.attributes.status),
    },
    { key: "clientCount", label: "Clients", sortable: true, render: (r) => r.attributes.clientCount },
    { key: "active", label: "Active", sortable: true, render: (r) => (r.attributes.active ? "Yes" : "No") },
    {
      key: "actions",
      label: "",
      render: (r) => {
        // A deactivated wealth manager can't log in, so inviting them is a dead
        // end — reactivating is the only meaningful action until they're back.
        const canInvite = r.attributes.active && r.attributes.status !== "onboarding_complete"
        const items: ActionItem[] = r.attributes.active
          ? [
              {
                label: "Deactivate",
                danger: true,
                onClick: () => setDeactivateTarget({ id: Number(r.id), name: r.attributes.fullName }),
              },
            ]
          : [
              {
                label: "Reactivate",
                onClick: () => reactivateMutation.mutate({ id: Number(r.id), name: r.attributes.fullName }),
              },
            ]

        return (
          <div className="fsp-admin__row-actions">
            {canInvite && (
              <button
                type="button"
                className="fsp-admin__row-link"
                onClick={() =>
                  inviteMutation.mutate({
                    id: Number(r.id),
                    name: r.attributes.fullName,
                    resend: r.attributes.status === "invited",
                  })
                }
                disabled={inviteMutation.isLoading}
              >
                {r.attributes.status === "invited" ? "Resend invite" : "Send invite"}
              </button>
            )}
            <RowActionsMenu items={items} />
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <div className="wb-clients__header">
        <div className="wb-clients__heading">
          <h1>Wealth Managers</h1>
          <div className="wb-clients__subtitle">
            {displayRows.length} {displayRows.length === 1 ? "wealth manager" : "wealth managers"}
          </div>
        </div>
        <div className="wb-clients__toolbar">
          <SearchBox onSearch={setSearch} placeholder="Search wealth managers" />
          <button type="button" className="wb-clients__add" onClick={() => setInviteOpen(true)}>+ Invite wealth manager</button>
        </div>
      </div>

      {error ? (
        <p>Failed to load wealth managers.</p>
      ) : (
        <Table
          data={displayRows}
          columns={columns}
          sort={sort}
          onSortChange={setSort}
          activeFilters={{ status: statusFilter }}
          onFilterChange={(_key, value) => setStatusFilter(value)}
          itemsPerPage={PER_PAGE}
          page={page}
          onPageChange={setPage}
          loading={isLoading}
          emptyMessage="No wealth managers found."
        />
      )}

      <InviteModal
        title="Invite Wealth Manager"
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={async (values: InviteFormValues) => {
          await createMutation.mutateAsync(values)
        }}
        submitting={createMutation.isLoading}
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate wealth manager"
        danger
        confirmLabel="Deactivate"
        busy={deactivateMutation.isLoading}
        message={
          deactivateTarget ? (
            <p style={{ margin: 0 }}>
              Deactivate <strong>{deactivateTarget.name}</strong>? They will lose access until reactivated.
            </p>
          ) : null
        }
        onConfirm={() => {
          if (!deactivateTarget) return
          deactivateMutation.mutate(deactivateTarget, { onSuccess: () => setDeactivateTarget(null) })
        }}
        onClose={() => setDeactivateTarget(null)}
      />
    </div>
  )
}
